import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

const EVENT_COLORS: Record<string, string> = {
    create: CHART_COLORS.chart.line2,
    budget: CHART_COLORS.chart.line3,
    status: CHART_COLORS.chart.line1,
    ended: CHART_COLORS.gray[500],
    creative: CHART_COLORS.chart.line4,
    targeting: CHART_COLORS.chart.line5,
    default: CHART_COLORS.gray[400],
};

const getEventColor = (eventType: string): string => {
    if (eventType.includes('create')) return EVENT_COLORS.create;
    if (eventType.includes('budget')) return EVENT_COLORS.budget;
    if (eventType.includes('status') || eventType.includes('run_status')) return EVENT_COLORS.status;
    if (eventType.includes('ended')) return EVENT_COLORS.ended;
    if (eventType.includes('creative')) return EVENT_COLORS.creative;
    if (eventType.includes('target')) return EVENT_COLORS.targeting;
    return EVENT_COLORS.default;
};

export const CampaignLifecycleChart: React.FC<BaseChartProps> = () => {
    useTranslation();
    const { events, isLoading } = useBoardChartData();

    const campaigns = useMemo(() => {
        if (!events) return [];

        const campaignEvents = events.filter((e: any) => e.category === 'campaign' && e.entityId);
        const byEntity = new Map<string, { name: string; events: any[] }>();

        campaignEvents.forEach((e: any) => {
            const eid = e.entityId;
            if (!byEntity.has(eid)) byEntity.set(eid, { name: e.description?.substring(0, 30) || eid.substring(0, 12), events: [] });
            byEntity.get(eid)!.events.push(e);
        });

        return Array.from(byEntity.values())
            .map(c => ({ ...c, events: c.events.sort((a: any, b: any) => (a.date || '').localeCompare(b.date || '')) }))
            .slice(0, 10);
    }, [events]);

    const isEmpty = !isLoading && campaigns.length === 0;
    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-3 overflow-y-auto">
                {campaigns.map((campaign, idx) => (
                    <div key={idx} className="mb-4 last:mb-0">
                        <div className="text-[10px] font-medium truncate mb-1" style={{ color: 'var(--text-primary)' }}>{campaign.name}</div>
                        <div className="flex items-center gap-1 flex-wrap">
                            {campaign.events.map((event: any, eIdx: number) => {
                                const color = getEventColor(event.type || event.eventType);
                                let dateLabel = '';
                                try { dateLabel = format(parseISO(event.date), 'MMM dd'); } catch { dateLabel = ''; }
                                return (
                                    <div key={eIdx} className="flex items-center gap-1">
                                        {eIdx > 0 && <div className="w-4 h-px" style={{ backgroundColor: CHART_COLORS.gray[300] }} />}
                                        <div className="group relative flex items-center">
                                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
                                                <div className="bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                                                    {dateLabel} - {event.type || event.eventType}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

            </div>
        </div>
    );
};
